import { ActionContext, Module } from "vuex";
import { State } from "@/store";
import { Optional } from "@/globalTypes";
import axios, { AxiosError, AxiosResponse } from "axios";
import {
  ActivationResponse,
  ForgotPasswordResponse,
  LoginErrorResponse,
  ObtainCSRFResponse,
  RegisterResponse,
  ResetPasswordResponse,
  User,
} from "@/accounts/types";
import { getOptional } from "@/utils";

interface AccountsState {
  user: Optional<User>;
  csrf: string;
}

export const store: Module<AccountsState, State> = {
  namespaced: true,
  state: () => ({
    user: null,
    csrf: "",
  }),
  getters: {
    user(state: AccountsState): Optional<User> {
      return state.user;
    },
    csrf(state: AccountsState): String {
      return state.csrf;
    },
  },
  actions: {
    login(
      context: ActionContext<AccountsState, State>,
      { email, password }: { email: String; password: String }
    ) {
      return new Promise((resolve) => {
        axios
          .post(
            "/accounts/user-login/",
            { email, password },
            { headers: { "X-CSRFToken": context.state.csrf } }
          )
          .then(() => {
            context.dispatch("getUser").then(() => {
              resolve({ success: true });
            });
          })
          .catch((reason: AxiosError<LoginErrorResponse>) => {
            const errors = {
              email: reason.response?.data.email
                ? reason.response.data.email[0]
                : null,
              password: reason.response?.data.password
                ? reason.response.data.password[0]
                : null,
              message: reason.response?.data.message
                ? reason.response.data.message[0]
                : null,
            };
            resolve({ success: false, errors });
          });
      });
    },
    register(context: ActionContext<AccountsState, State>, email: String) {
      return new Promise((resolve) => {
        axios
          .post("/accounts/register/", { email })
          .then((result) => {
            resolve({ success: true, result: result.data });
          })
          .catch((reason: AxiosError<RegisterResponse>) => {
            const errors = reason.response!.data?.email;
            const resolveErrors = errors
              ? errors[0]
              : "Nieoczekiwany błąd. Spróbuj ponownie lub skontaktuj się z pomocą techniczną.";
            resolve({ success: false, errors: resolveErrors });
          });
      });
    },
    activation(
      context: ActionContext<AccountsState, State>,
      { uidb64, token }: { uidb64: String; token: String }
    ): Promise<String> {
      return new Promise((resolve) => {
        axios
          .get(`/accounts/activate/${uidb64}/${token}/`)
          .then((response: AxiosResponse<ActivationResponse>) => {
            context.dispatch("getUser").then(() => {
              resolve(response.data.status);
            });
          });
      });
    },
    obtainCSRF(context: ActionContext<AccountsState, State>): Promise<void> {
      return new Promise((resolve) => {
        axios
          .get("/accounts/obtain-csrf/")
          .then((response: AxiosResponse<ObtainCSRFResponse>) => {
            context.state.csrf = response.data.csrf;
            resolve();
          });
      });
    },
    logout(context: ActionContext<AccountsState, State>): Promise<void> {
      return new Promise((resolve) => {
        axios.get("/accounts/logout").then(() => {
          context.state.user = null;
          this.dispatch("cars/pullCars").then(() => resolve());
        });
      });
    },
    getUser(context: ActionContext<AccountsState, State>): Promise<void> {
      return new Promise((resolve) => {
        axios
          .get("/api/user/")
          .then((response: AxiosResponse<User>) => {
            context.state.user = response.data;
            context.dispatch("obtainCSRF").then(() => resolve());
          })
          .catch(() => {
            context.state.user = null;
            context.dispatch("obtainCSRF").then(() => resolve());
          });
      });
    },
    requestResetPassword(
      context: ActionContext<AccountsState, State>,
      email: string
    ) {
      return new Promise((resolve) => {
        axios
          .post(
            "/accounts/request-reset-email/",
            { email },
            { headers: { "X-CSRFToken": context.state.csrf } }
          )
          .then(() => {
            resolve({ success: true });
          })
          .catch((reason: AxiosError<ForgotPasswordResponse>) => {
            resolve({
              success: false,
              error: {
                email: getOptional(reason.response!.data.email, 0),
                message: reason.response!.data.message,
              },
            });
          });
      });
    },
    newPassword(
      context: ActionContext<AccountsState, State>,
      {
        password,
        repeatPassword,
        uidb64,
        token,
      }: {
        password: string;
        repeatPassword: string;
        uidb64: string;
        token: string;
      }
    ) {
      return new Promise((resolve) => {
        axios
          .put(
            `/accounts/password-reset/${uidb64}/${token}/`,
            { newPassword: password, newPasswordConfirmation: repeatPassword },
            { headers: { "X-CSRFToken": context.state.csrf } }
          )
          .then(() => {
            context.dispatch("getUser").then(() => {
              resolve({ success: true });
            });
          })
          .catch((reason: AxiosError<ResetPasswordResponse>) => {
            const errors = {
              password: getOptional(reason.response!.data.newPassword, 0),
              repeatPassword: getOptional(
                reason.response!.data.newPasswordConfirmation,
                0
              ),
              nonFieldErrors: getOptional(
                reason.response!.data.nonFieldErrors,
                0
              ),
              globalError: getOptional(reason.response!.data.message, 0),
            };
            resolve({ success: false, errors });
          });
      });
    },
  },
};
